import { useOutletContext } from 'react-router-dom'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { DatePickerValue, F0Alert, F0DatePicker } from '@factorialco/f0-react'
import {
  Input,
  NumberInput,
  Select,
  Switch,
  Textarea,
} from '@factorialco/f0-react/dist/experimental'
import { ProjectManagementValueObjectsBudgetStrategyTypeEnum } from 'generated/resources/graphql'

import { FormErrors } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import {
  SubprojectBasicDetails,
  SubprojectFinancialDetails,
  SubprojectsSettingsFormValues,
} from 'modules/project_management/components/tables/SubprojectsDataCollection/types'
import { UpsellFixedTimeBudgetSettings } from 'modules/project_management/components/upsells/UpsellFixedTimeBudgetSettings'
import { budgetTypesEnum } from 'modules/project_management/lib/constants'
import { statusOptions } from 'modules/project_management/lib/projectStatus'
import { ISubprojectsPanelData } from 'modules/project_management/pages/ProjectDetailsPage/tabs/SubprojectsPage/types'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

interface SubprojectsSettingsTabProps {
  subprojectId: number
  subprojectData?: ISubprojectsPanelData
  formValues: SubprojectsSettingsFormValues
  formErrors: FormErrors<SubprojectsSettingsFormValues>
  onBasicDetailsChange: (
    fieldName: keyof SubprojectBasicDetails,
    fieldValue: string | undefined | boolean
  ) => void
  onFinancialDetailsChange: (
    fieldName: keyof SubprojectFinancialDetails,
    fieldValue: number | boolean | undefined
  ) => void
  subprojectDetails: {
    subprojectName?: string
    subprojectId: number
    description?: string
    status: string
    code?: string
    startDate?: string
    dueDate?: string
  }
  currency?: string
  budgetType?: ProjectManagementValueObjectsBudgetStrategyTypeEnum | undefined
  delegated: boolean
  projectId: number
  isOwnerOrAdmin: boolean
}

const SubprojectsSettingsTab = ({
  formValues,
  formErrors,
  onBasicDetailsChange,
  onFinancialDetailsChange,
  subprojectDetails,
  currency,
  delegated,
  budgetType,
  projectId,
  isOwnerOrAdmin,
}: SubprojectsSettingsTabProps) => {
  const statusType = statusOptions({ action: 'create' }).map((status) => ({
    label: status.label,
    value: status.value,
  }))
  const { navigation } = useNavigation()

  const { hasBudgetFeature } = useOutletContext<{
    hasBudgetFeature: boolean
  }>()

  const { canRequestUpsell } = useCurrentSession()

  const handleBasicDetailsFieldChanged = (
    fieldName: keyof SubprojectBasicDetails,
    fieldValue: string | undefined | boolean
  ) => {
    onBasicDetailsChange(fieldName, fieldValue)
  }

  const handleFinancialDetailsFieldChanged = (
    fieldName: keyof SubprojectFinancialDetails,
    fieldValue: number | boolean | undefined
  ) => {
    onFinancialDetailsChange(fieldName, fieldValue)
  }

  const dateSelected = (
    value: DatePickerValue | undefined,
    fieldName: keyof SubprojectBasicDetails
  ) => {
    const selectedDate = value?.value?.from

    if (!selectedDate) {
      handleBasicDetailsFieldChanged(fieldName, undefined)
      return
    }

    handleBasicDetailsFieldChanged(fieldName, i18nDateToString(selectedDate, 'backend'))
  }

  const stringToDatePickerValue = (dateString: string | undefined): DatePickerValue | undefined => {
    if (!dateString) return undefined

    const parsedDate = stringToDate(dateString, 'backend')

    if (!parsedDate.isValid()) return undefined

    const normalizedDate = parsedDate.toDate()

    return {
      granularity: 'day',
      value: {
        from: normalizedDate,
        to: normalizedDate,
      },
    }
  }

  const renderFinancialDetailsContent = () => {
    if (!hasBudgetFeature) {
      if (canRequestUpsell) {
        return (
          <Box
            gap="s16"
            style={{
              transition: 'max-height 150ms ease-in-out, opacity 150ms ease-in-out',
              maxHeight: '800px',
            }}
          >
            <Text color="textPrimary" size="300" weight="medium">
              {i18n.t('project_management.overview.sections.financial_details.title')}
            </Text>
            <UpsellFixedTimeBudgetSettings />
          </Box>
        )
      }
      return null
    }
    if (!delegated) {
      return (
        <Box
          gap="s16"
          style={{
            transition: 'max-height 150ms ease-in-out, opacity 150ms ease-in-out',
            maxHeight: '800px',
          }}
        >
          <Text color="textPrimary" size="300" weight="medium">
            {i18n.t('project_management.overview.sections.financial_details.title')}
          </Text>
          <F0Alert
            description={i18n.t(
              'project_management.settings.sections.budget_strategies_section.phases_one_alert.description'
            )}
            link={{
              href: `${navigation.projects.id.details.settings.fullPath({ id: projectId })}`,
              label: i18n.t(
                'project_management.settings.sections.budget_strategies_section.phases_one_alert.label'
              ),
            }}
            title={i18n.t(
              'project_management.settings.sections.budget_strategies_section.phases_one_alert.title'
            )}
            variant="info"
          />
        </Box>
      )
    }
    return (
      <Box
        gap="s16"
        style={{
          transition: 'max-height 150ms ease-in-out, opacity 150ms ease-in-out',
          maxHeight: '800px',
        }}
      >
        <Text color="textPrimary" size="300" weight="medium">
          {i18n.t('project_management.overview.sections.financial_details.title')}
        </Text>
        <Box gap="s16">
          {budgetType !== budgetTypesEnum.withoutBudget && (
            <NumberInput
              locale={i18n.locale}
              size="md"
              label={i18n.t(
                'project_management.settings.sections.budget_strategies_section.common.estimated_cost_title'
              )}
              placeholder="0"
              hint={i18n.t(
                'project_management.settings.sections.budget_strategies_section.common.estimated_cost_subtitle'
              )}
              value={formValues.financialDetails.estimatedCost ?? null}
              onChange={(value) =>
                handleFinancialDetailsFieldChanged('estimatedCost', value ?? undefined)
              }
              units={currency}
              error={formErrors.financialDetails?.estimatedCost}
              required={budgetType == budgetTypesEnum.projectFixedCost}
            />
          )}
          <NumberInput
            locale={i18n.locale}
            size="md"
            label={i18n.t('project_management.forms.financial_details.fixed_costs.label')}
            placeholder="0"
            value={formValues.financialDetails.fixedCost ?? null}
            onChange={(value) =>
              handleFinancialDetailsFieldChanged('fixedCost', value ?? undefined)
            }
            units={currency}
            error={formErrors.financialDetails?.fixedCost}
          />
          {budgetType === budgetTypesEnum.totalBudget && (
            <>
              <Divider />
              <NumberInput
                locale={i18n.locale}
                size="md"
                label={i18n.t('time_tracking.estimated.title')}
                placeholder="0"
                value={formValues.financialDetails.estimatedHours ?? null}
                onChange={(value) =>
                  handleFinancialDetailsFieldChanged('estimatedHours', value ?? undefined)
                }
                units="h"
                error={formErrors.financialDetails?.estimatedHours}
              />
              <Divider />
            </>
          )}

          <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
            <Text.Normal size="200">
              {i18n.t('project_management.forms.financial_details.billableToggle.label')}
            </Text.Normal>
            <Switch
              hideLabel
              onCheckedChange={(isBillable) =>
                handleFinancialDetailsFieldChanged('isBillable', isBillable)
              }
              checked={formValues.financialDetails.isBillable || false}
            />
          </Box>

          {formValues.financialDetails.isBillable &&
            budgetType !== budgetTypesEnum.withoutBudget && (
              <NumberInput
                locale={i18n.locale}
                size="md"
                label={
                  budgetType === budgetTypesEnum.projectFixedCost
                    ? i18n.t('finance.chart_of_accounts.types.income')
                    : i18n.t(
                        'project_management.forms.project_planning.project_budget.labels.total_budget.title'
                      )
                }
                placeholder="0"
                hint={
                  budgetType !== budgetTypesEnum.projectFixedCost
                    ? i18n.t(
                        'project_management.forms.project_planning.project_budget.labels.total_budget.description'
                      )
                    : undefined
                }
                value={formValues.financialDetails.feeAmount ?? null}
                onChange={(value) =>
                  handleFinancialDetailsFieldChanged('feeAmount', value ?? undefined)
                }
                units={currency}
                error={formErrors.financialDetails?.feeAmount}
              />
            )}
        </Box>
      </Box>
    )
  }

  return (
    <Box gap="s32">
      <Box gap="s16">
        <Text color="textPrimary" size="300" weight="medium">
          {i18n.t('project_management.settings.sections.basic_information.title')}
        </Text>

        <Input
          size="md"
          label={i18n.t('project_management.table.projects_list.headers.name')}
          placeholder={
            subprojectDetails.subprojectName || i18n.t('project_management.export.subproject_name')
          }
          value={formValues.basicDetails.name || ''}
          onChange={(value) => handleBasicDetailsFieldChanged('name', value || undefined)}
          error={formErrors.basicDetails?.name}
          required
        />
        <Input
          size="md"
          label={i18n.t('project_management.table.projects_list.headers.code')}
          placeholder={'SP01'}
          value={formValues.basicDetails.code || ''}
          onChange={(value) => handleBasicDetailsFieldChanged('code', value || undefined)}
          error={formErrors.basicDetails?.code}
        />
        <Box flexDirection="row" gap="s16" width="full">
          <Box width="full">
            <F0DatePicker
              label={i18n.t('project_management.imports.table.headers.start_date')}
              onChange={(value) => dateSelected(value, 'startDate')}
              placeholder="MM/DD/YYYY"
              value={stringToDatePickerValue(formValues.basicDetails.startDate)}
              maxDate={
                formValues.basicDetails.dueDate
                  ? stringToDate(formValues.basicDetails.dueDate, 'backend').toDate()
                  : undefined
              }
            />
          </Box>
          <Box width="full">
            <F0DatePicker
              label={i18n.t('project_management.imports.table.headers.due_date')}
              onChange={(value) => dateSelected(value, 'dueDate')}
              placeholder="MM/DD/YYYY"
              value={stringToDatePickerValue(formValues.basicDetails.dueDate)}
              minDate={
                formValues.basicDetails.startDate
                  ? stringToDate(formValues.basicDetails.startDate, 'backend').toDate()
                  : undefined
              }
            />
          </Box>
        </Box>
        <Select
          label={i18n.t('project_management.imports.table.headers.status')}
          value={formValues.basicDetails.status}
          onChange={(value: string) => handleBasicDetailsFieldChanged('status', value)}
          options={statusType}
          error={formErrors.basicDetails?.status}
          required
        />
        <Textarea
          label={i18n.t(
            'project_management.modals.create_project.input_labels.project_description'
          )}
          placeholder={i18n.t(
            'project_management.modals.create_project.input_labels.project_description'
          )}
          value={formValues.basicDetails.description || ''}
          onChange={(value) => handleBasicDetailsFieldChanged('description', value || undefined)}
          error={formErrors.basicDetails?.description}
          rows={5}
        />
      </Box>

      <>{isOwnerOrAdmin && <>{renderFinancialDetailsContent()}</>}</>
    </Box>
  )
}

export default SubprojectsSettingsTab
